// Transport layer for article generation (worker API + Directus queue fallback).
// Moved out of CreateArticle.js without changing requests, payloads or timeouts.
// The `log` calls are the original no-op debug hooks (kept for parity).
import axios from 'axios';
import { directusAPI } from '../../../api';
import { isFailedArticleState } from '../../../pages/Articles/articleStatus';
import { hasPersistedArticleBody } from '../lib/editorBlocks';
import {
  getArticleEndpointUrls,
  getDirectusBaseUrl,
  getDraftEndpointUrls,
  getHealthCheckUrl,
  isLocalhost,
  mapQueueStatusToLoaderText,
} from '../lib/workerEndpoints';

// Clean logging function - disable all debug logs
// eslint-disable-next-line no-unused-vars
export const log = (...args) => {
  // All logs disabled for clean UI
};

/**
 * Get stored authentication token
 */
export const getStoredToken = () => {
  return localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
};

/**
 * Queue API (available in all environments via Directus queue fields).
 * `updateLoaderProgress` receives polling progress for the full-screen loader.
 */
export const createQueueAPI = ({ updateLoaderProgress }) => ({
  // In localhost force API-only generation to avoid production queue mixing.
  isQueueAvailable() {
    return !isLocalhost();
  },

  // Add article to queue
  async addToQueue(articleData) {
    try {
      log('\n=== QUEUE API: ADD TO QUEUE ===');
      log('Article Data:', articleData);
      log('Article ID:', articleData.id);

      const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
      log('Auth Token Available:', !!token);
      log('Auth Token (first 20 chars):', token ? token.substring(0, 20) + '...' : 'No token');

      if (!token) {
        throw new Error('No authorization token found in localStorage or sessionStorage');
      }

      if (!articleData.id) {
        throw new Error('Article ID is missing from articleData');
      }

      const patchData = {
        queue_status: 'queued',
        queue_position: 1
      };
      log('PATCH Data:', patchData);

      const baseURL = getDirectusBaseUrl();
      const url = `${baseURL}/items/articles/${articleData.id}`;
      log('PATCH URL:', url);

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };
      log('Request Config (headers only):', Object.keys(config.headers));

      log('Making PATCH request...');
      const response = await axios.patch(url, patchData, config);

      log('PATCH Response Status:', response.status);
      log('PATCH Response Data:', response.data);

      // Verify the queue_status was actually set
      log('Verifying queue_status was set...');
      const verifyResponse = await axios.get(`${baseURL}/items/articles/${articleData.id}?fields=id,queue_status,queue_position`, config);
      log('Verification Response:', verifyResponse.data);

      log('=== END QUEUE API: ADD TO QUEUE ===\n');

      return response.data;
    } catch (error) {
      log('\n=== QUEUE API: ADD TO QUEUE ERROR ===');
      log('Error Message:', error.message);
      log('Error Status:', error.response?.status);
      log('Error Data:', error.response?.data);
      log('Error Config URL:', error.config?.url);
      log('Error Config Method:', error.config?.method);
      log('Error Config Data:', error.config?.data);

      if (error.response?.status === 403) {
        log('❌ QUEUE API: 403 Forbidden - Token expired or invalid');
      } else if (error.response?.status === 404) {
        log('❌ QUEUE API: 404 Not Found - Article does not exist');
      } else if (error.response?.status === 422) {
        log('❌ QUEUE API: 422 Validation Error - Invalid field data');
      }

      log('=== END QUEUE API: ADD TO QUEUE ERROR ===\n');
      throw error;
    }
  },

  // Poll article status
  async pollStatus(articleId, onStatusUpdate) {
    const pollInterval = 2000; // 2 seconds
    let attempts = 0;
    const maxAttempts = 150; // 5 minutes max

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;
          const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');

          const baseURL = getDirectusBaseUrl();
          const response = await axios.get(`${baseURL}/items/articles/${articleId}?fields=id,queue_status,processing_started_at,processing_completed_at,error_message,article_draft,title,thread_id`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          const article = response.data.data;

          log(`📊 POLL: ${articleId} status: ${article.queue_status}`);

          if (onStatusUpdate) {
            onStatusUpdate(article);
          }

          updateLoaderProgress({
            phase: 'queue_poll',
            articleId,
            queueStatus: article.queue_status || '',
            detail: mapQueueStatusToLoaderText(article.queue_status)
          });

          if (article.queue_status === 'completed' || article.queue_status === 'draft_ready') {
            log('✅ POLL SUCCESS: Article generation completed');
            resolve(article);
          } else if (isFailedArticleState(article)) {
            log('POLL ERROR: Article generation failed:', article.error_message);
            reject(new Error(article.error_message || 'Article generation failed'));
          } else if (attempts >= maxAttempts) {
            log('POLL TIMEOUT: Polling timeout reached');
            reject(new Error('Article generation timeout'));
          } else {
            // Continue polling
            log(`POLL CONTINUE: Status is "${article.queue_status}", continuing polling in ${pollInterval}ms...`);
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          log('Polling error:', error);

          // Specific handling for 401 Unauthorized errors
          if (error.response?.status === 401) {
            log('POLL AUTH ERROR: 401 Unauthorized - token may have expired');
            log('POLL AUTH ERROR: Response:', error.response?.data);

            // Check if token still exists
            const currentToken = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
            log(`POLL AUTH ERROR: Current token exists: ${!!currentToken}`);

            // For now, reject with a clear error message
            reject(new Error('Authentication failed during polling - token may have expired. Please refresh the page and try again.'));
            return;
          }

          if (attempts >= maxAttempts) {
            reject(error);
          } else {
            setTimeout(poll, pollInterval);
          }
        }
      };

      poll();
    });
  },
});

/**
 * Persists a terminal failure unless the article already succeeded server-side.
 */
export const markArticleFailed = async (articleId) => {
  if (!articleId) return;

  try {
    // Never overwrite a successful generation just because the browser request timed out.
    // Backend may finish and persist body/status after the UI already got a network/timeout error.
    const latest = await directusAPI.get(`/items/articles/${articleId}`, {
      params: {
        fields: 'id,status,queue_status,article_body,article_draft'
      },
      headers: {
        Authorization: `Bearer ${getStoredToken()}`
      }
    });
    const article = latest?.data?.data;
    const terminalSuccessStatuses = new Set(['completed', 'success', 'draft_ready']);
    const alreadySucceeded =
      terminalSuccessStatuses.has(String(article?.status || ''))
      || terminalSuccessStatuses.has(String(article?.queue_status || ''))
      || hasPersistedArticleBody(article?.article_body);

    if (alreadySucceeded) {
      log('⏭️ SKIP markArticleFailed: article already has persisted success/content', {
        articleId,
        status: article?.status,
        queue_status: article?.queue_status,
        hasBody: hasPersistedArticleBody(article?.article_body)
      });
      return;
    }

    await directusAPI.patch(`/items/articles/${articleId}`, {
      status: 'failed',
      queue_status: 'failed'
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getStoredToken()}`
      }
    });
  } catch (statusError) {
    log('Unable to persist failed article status:', statusError);
  }
};

/**
 * API function to generate full article via separate endpoint
 */
export const generateArticleViaAPI = async (articleId, threadId) => {
  try {
    const token = localStorage.getItem('directus_token');

    log('🚀 API ARTICLE: Calling article generation endpoint');
    log('📋 API ARTICLE: Article ID:', articleId);
    log('🔗 API ARTICLE: Thread ID:', threadId);

    // Localhost strict mode: hit local API only. Never send local articles to production.
    const articleUrls = getArticleEndpointUrls();

    let lastError = null;
    for (const articleUrl of articleUrls) {
      try {
        const response = await axios.post(articleUrl, {
          articleId: articleId
          // The worker uses the article's own thread_id (server-side) and the
          // user from the token; a client thread id is never sent.
        }, {
          // Keep aligned with api-server ARTICLE_GENERATION_TIMEOUT (20 min).
          timeout: 20 * 60 * 1000,
          retry: 1,
          retryDelay: 5000,
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success) {
          log('✅ API ARTICLE: Generation successful');
          log('✅ API ARTICLE: Response:', response.data);
          return {
            success: true,
            fullArticle: response.data.fullArticle,
            article: response.data.article
          };
        }

        throw new Error(response.data.error || 'Article generation failed');
      } catch (requestError) {
        lastError = requestError;
        log(`❌ API ARTICLE: Endpoint failed (${articleUrl})`, requestError?.message);
      }
    }
    throw lastError || new Error('Article generation failed');
  } catch (error) {
    log('❌ API ARTICLE ERROR:', error);
    log('❌ API ARTICLE ERROR RESPONSE:', error.response?.data);
    throw error;
  }
};

/**
 * Check if API server is available
 */
export const isApiServerAvailable = async () => {
  try {
    log('🔍 API SERVER: Checking availability at /api/health');
    // Use localhost proxy in development, external API in production
    const healthUrl = getHealthCheckUrl();
    const response = await axios.get(healthUrl, { timeout: 5000 });
    log('✅ API SERVER: Response received:', response.data);
    const status = String(response?.data?.status || '').toLowerCase();
    const isLocal = isLocalhost();
    const openaiOk = response?.data?.services?.openai?.status === 'ok';

    // Local strict mode: if endpoint responds at all and OpenAI is OK, still use API.
    // Directus health may show degraded without service credentials, but user-token requests can still work.
    if (isLocal && (status === 'healthy' || status === 'degraded') && openaiOk) {
      log('✅ API SERVER: AVAILABLE (local degraded accepted) - Using API endpoints');
      return true;
    }

    if (status === 'healthy') {
      log('✅ API SERVER: AVAILABLE - Using API endpoints');
      return true;
    }

    log('❌ API SERVER: Response not healthy:', response.data);
    return false;
  } catch (error) {
    log('❌ API SERVER: NOT AVAILABLE - Error details:');
    log('❌ API SERVER: Error message:', error.message);
    log('❌ API SERVER: Error code:', error.code);
    log('❌ API SERVER: Error response:', error.response?.data);
    log('❌ API SERVER: Error status:', error.response?.status);

    if (error.code === 'ERR_NETWORK' || error.message.includes('CORS')) {
      log('❌ API SERVER: CORS or Network error detected');
    }

    log('🔄 API SERVER: Falling back to queue system');
    return false;
  }
};

/**
 * Posts the draft request to each worker endpoint until one succeeds.
 * Mirrors the loop previously inlined in autoGenerateViaAPI.
 */
export const requestDraftFromWorkers = async (requestPayload, { updateLoaderProgress }) => {
  let result = null;
  // Define multiple endpoint URLs to try
  const endpointUrls = getDraftEndpointUrls();

  let lastError = null;
  let successfulEndpoint = null;

  // Get token for authorization
  const token = localStorage.getItem('directus_token');

  // Try each endpoint until one works
  for (let i = 0; i < endpointUrls.length; i++) {
    const draftUrl = endpointUrls[i];
    try {
      log(`🔄 API DRAFT: Trying endpoint ${i + 1}/${endpointUrls.length}: ${draftUrl}`);
      updateLoaderProgress({
        phase: 'draft_request',
        attempt: i + 1,
        totalAttempts: endpointUrls.length,
        detail: 'Przygotowujemy szkic i porzadkujemy najwazniejsze informacje'
      });

      const response = await axios.post(draftUrl, requestPayload, {
        timeout: 300000,
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json'
        }
      });

      log(`✅ API DRAFT: Success with endpoint: ${draftUrl}`);
      successfulEndpoint = draftUrl;

      if (response.data.success) {
        log('✅ API DRAFT: Generation successful');
        log('✅ API DRAFT: Response:', response.data);
        result = {
          success: true,
          draft: response.data.draft,
          threadId: response.data.threadId,
          article: response.data.article
        };
        break; // Success! Exit the loop
      } else {
        throw new Error(response.data.error || 'Draft generation failed');
      }
    } catch (error) {
      log(`❌ API DRAFT: Endpoint ${draftUrl} failed:`, error.message);
      log('❌ API DRAFT: Error status:', error.response?.status);
      log('❌ API DRAFT: Error body:', error.response?.data);
      const status = error.response?.status;
      const backendError = error.response?.data?.error;
      const backendMessage = error.response?.data?.message;
      const detailObject = error.response?.data?.detail;
      const detailString = typeof detailObject === 'string'
        ? detailObject
        : (detailObject?.message || '');
      const bestMessage = String(
        backendError
        || backendMessage
        || detailString
        || error.message
        || 'Draft generation failed'
      ).trim();
      lastError = new Error(status ? `Draft API (${status}): ${bestMessage}` : bestMessage);

      // If this is not the last endpoint, continue to next one
      if (i < endpointUrls.length - 1) {
        log(`🔄 API DRAFT: Trying next endpoint...`);
        continue;
      } else {
        log('❌ API DRAFT: All endpoints failed');
        log('❌ API DRAFT: Last error:', lastError);
      }
    }
  }

  // If no endpoint worked, throw the last error
  if (!result) {
    throw lastError || new Error('All draft generation endpoints failed');
  }

  return { result, successfulEndpoint };
};
